import React, { useEffect, useState } from 'react'
import { reduxForm, Field } from 'redux-form'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast, cssTransition } from 'react-toastify'

import WrappedInput from 'components/Shared/Forms/WrappedInput'
import Button from 'components/Shared/Button'
import UserDeletionModal from 'components/Admin/UserDeletionModal'
import EmptyState from 'components/Shared/EmptyState'
import Loading from 'components/Shared/Loading'
import Page from 'components/Shared/Page'
import AppIcon from 'components/Shared/AppIcon'

import {
  requestUser,
  getUser,
  isUserDeleted,
  isUserMarketingUnsubscribed,
} from 'ducks/admin/users'
import { isCurrentUserAdaloAdmin } from 'ducks/users'

import { adminDisable2faForUser, adminUpdatePassword } from 'utils/io'
import { adaloBackendAxios } from 'utils/io/http/axios'

import AdminUserOrganization from './AdminUserOrganization'

import './Admin.scss'

const FORM_NAME = 'adminResetPasswordForm'

const displayAttribution = value =>
  value == null || value === '' ? '—' : String(value)

const SignupAttributionSection = ({ user }) => {
  const rows = [
    { label: 'GA4 client ID', value: user.ga4ClientId },
    { label: 'First referrer', value: user.firstReferrer },
    { label: 'Landing page URL', value: user.lpUrl },
    { label: 'UTM / campaign URL', value: user.utm },
    { label: 'Visitor ID', value: user.visitorId },
  ]

  return (
    <div className="admin-user-attribution">
      <h4>Signup attribution</h4>
      <p className="admin-user-attribution-note">
        Values captured at account creation (marketing cookies on app). Empty if
        not available or recorded before this feature shipped.
      </p>
      <dl className="admin-user-attribution-dl">
        {rows.map(({ label, value }) => (
          <React.Fragment key={label}>
            <dt className="admin-user-attribution-dt">{label}</dt>
            <dd className="admin-user-attribution-dd">
              {displayAttribution(value)}
            </dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  )
}

const AppItem = ({ app, userId }) => {
  const [datasources, setDatasources] = useState([])

  useEffect(() => {
    Promise.all([adaloBackendAxios.get(`/apps/${app.id}/datasources`)])
      .then(([result]) => {
        const dbs = Object.keys(result.data)
        setDatasources(dbs)
      })
      .catch(err => {
        toast(
          <>
            <div>
              <h2>Error</h2>
              <p>
                Failed to load datasources: {err.message || 'Unknown error'}
              </p>
            </div>
          </>,
          {
            position: 'bottom-right',
            className: 'generic-error__toast',
            bodyClassName: 'generic-error__toast-body',
            hideProgressBar: true,
            autoClose: 5000,
            transition: cssTransition({
              enter: 'animate__animated animate__fadeInUp',
              exit: 'animate__animated animate__fadeOutDown',
              collapse: false,
            }),
          }
        )
      })
  }, [app.id])

  return (
    <Link className="admin-user-item" to={`/admin/users/${userId}/${app.id}`}>
      <AppIcon app={app} />
      <h5>{app.name}</h5>
      <p>{app.createdAt}</p>
      <p>{app.updatedAt}</p>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {datasources.map(id => (
          <Link key={id} to={`/admin/datasource/${id}`}>
            {id}
          </Link>
        ))}
      </div>
    </Link>
  )
}

const UserDeletionSection = ({ user }) => {
  const dispatch = useDispatch()
  const isAdaloAdmin = useSelector(isCurrentUserAdaloAdmin)
  const userDeleted = useSelector(state => isUserDeleted(state, user.id))
  const userMarketingUnsubscribed = useSelector(state =>
    isUserMarketingUnsubscribed(state, user.id)
  )
  const [showDeletionModal, setShowDeletionModal] = useState(false)

  if (!isAdaloAdmin) {
    return null
  }

  const handleShowDeletionModal = () => {
    setShowDeletionModal(true)
  }

  const handleCloseDeletionModal = () => {
    setShowDeletionModal(false)
  }

  const handleDeletionSuccess = () => {
    // Refetch user data to update the UI without losing application state
    dispatch(requestUser(user.id))
  }

  return (
    <div className="admin-user-deletion-section">
      <h4>User Management</h4>

      {/* Status indicators */}
      <div className="user-status-indicators">
        {userDeleted && (
          <span className="user-deleted-indicator">
            <span role="img" aria-label="deleted">
              🗑️
            </span>{' '}
            User Deleted
          </span>
        )}
        {userMarketingUnsubscribed && (
          <span className="user-marketing-unsubscribed-indicator">
            <span role="img" aria-label="email">
              📧
            </span>{' '}
            Marketing Unsubscribed
          </span>
        )}
        {user.deletedAt && (
          <span className="user-deleted-indicator">
            <span role="img" aria-label="deleted">
              🗑️
            </span>{' '}
            Anonymized on {new Date(user.deletedAt).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Action buttons */}
      {!userDeleted && (
        <div className="user-deletion-actions">
          <Button onClick={handleShowDeletionModal} color="red" outlined large>
            Manage User Data
          </Button>
        </div>
      )}

      <UserDeletionModal
        user={user}
        isOpen={showDeletionModal}
        onClose={handleCloseDeletionModal}
        onSuccess={handleDeletionSuccess}
      />
    </div>
  )
}

const AdminUserDetail = ({ match }) => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(requestUser(match.params.userId))
  }, [dispatch, match.params.userId])

  const user = useSelector(state => getUser(state, match.params.userId))

  if (!user) {
    return (
      <Page className="admin-users-page" title="Loading User...">
        <Link to="/admin/users">← Back to Users</Link>
        <EmptyState greedy>
          <Loading large />
        </EmptyState>
      </Page>
    )
  }

  const apps = user.Apps || []

  return (
    <Page className="admin-users-page" title={`User: ${user.name}`}>
      <Link to="/admin/users">← Back to Users</Link>

      <div className="admin-user-header">
        <div>
          <h1>{user.name}</h1>
          <h2>{user.email}</h2>
        </div>
      </div>

      {/* User deletion/management section */}
      <UserDeletionSection user={user} />

      <SignupAttributionSection user={user} />

      <div className="admin-user-security">
        <h4>Security Actions</h4>
        <ChangePasswordForm userId={user.id} />
        {user.otpEnabled && <Disable2FaAuthentication userId={user.id} />}
      </div>

      <div className="admin-user-organizations">
        <h4>Organizations</h4>
        {user.Organizations && user.Organizations.length > 0 ? (
          user.Organizations.map(org => (
            <AdminUserOrganization key={org.id} organization={org} />
          ))
        ) : (
          <p>No organizations</p>
        )}
      </div>

      <div className="admin-user-apps">
        <h4>Apps ({apps.length})</h4>
        {apps.length > 0 ? (
          apps.map(app => <AppItem key={app.id} app={app} userId={user.id} />)
        ) : (
          <p>No apps</p>
        )}
      </div>
    </Page>
  )
}

const ChangePasswordForm = ({ userId }) => {
  const isAdaloAdmin = useSelector(isCurrentUserAdaloAdmin)
  const [password, setPassword] = useState('')

  if (!isAdaloAdmin) {
    return null
  }

  const verifyPasswordChange = async e => {
    e.preventDefault()

    if (
      window.confirm(`Are you sure you want to reset this user's password?`)
    ) {
      try {
        await adminUpdatePassword({ password, userId })

        setPassword('')
        toast.success('Successfully reset the password!')
      } catch (err) {
        console.error(`Could not update password: ${err.message}`)
        toast.error(`Failed to update password: ${err.message}`)
      }
    }
  }

  return (
    <>
      <form
        onSubmit={verifyPasswordChange}
        className="admin-reset-password-form"
      >
        <input
          type="text"
          name="email"
          autoComplete="username email"
          style={{ display: 'none' }}
        />
        <Field
          autoFocus
          name="password"
          placeholder="New Password"
          autoComplete="new-password"
          type="password"
          label="New Password"
          className="admin-password-field"
          maxLength={200}
          onChange={e => setPassword(e.target.value)}
          component={WrappedInput}
          value={password}
        />
        <Button large yellow>
          Change Password
        </Button>
      </form>
    </>
  )
}

const Disable2FaAuthentication = ({ userId }) => {
  const isAdaloAdmin = useSelector(isCurrentUserAdaloAdmin)
  const [isDisabled, setIsDisabled] = useState(false)

  if (!isAdaloAdmin) {
    return null
  }

  const disable2FA = async e => {
    e.preventDefault()

    if (
      !window.confirm(
        `Are you sure you want to disable 2 Factor Authentication for this user?`
      )
    ) {
      return
    }

    try {
      await adminDisable2faForUser({ userId })

      setIsDisabled(true)
      toast.success('Successfully disabled 2FA for this user')
    } catch (err) {
      toast.error(`Failed to disable 2FA: ${err.message}`)
    }
  }

  return (
    <>
      {!isDisabled && (
        <div className="admin-disable-2fa-wrapper">
          <form className="admin-disable-2fa-form" onSubmit={disable2FA}>
            <Button className="admin-disable-2fa-button" large outlined teal>
              Disable 2FA
            </Button>
          </form>
        </div>
      )}
    </>
  )
}

export default reduxForm({
  form: FORM_NAME,
})(AdminUserDetail)
